import React from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';

/**
 * Reusable FormField component for registration form
 * 
 * @param {Object} props - Component props
 * @param {string} props.type - Input type (text, password, date, dropdown, etc)
 * @param {string} props.id - Input ID
 * @param {string} props.label - Input label
 * @param {string} props.name - Input name
 * @param {any} props.value - Input value
 * @param {function} props.onChange - Change handler function
 * @param {string} props.placeholder - Input placeholder
 * @param {boolean} props.hasError - Whether field has validation error
 * @param {string} props.errorMessage - Error message to display if hasError is true
 * @param {Array} props.options - Options for dropdown type
 * @param {string} props.icon - Icon for input group
 * @param {boolean} props.disabled - Whether field is disabled
 * @param {Object} props.dateProps - Additional props for date picker
 * @param {string} props.className - Additional CSS classes
 */
const FormField = ({ 
  type = 'text', 
  id, 
  label, 
  name, 
  value, 
  onChange, 
  placeholder = '', 
  hasError = false, 
  errorMessage = '', 
  options = [], 
  icon = null,
  disabled = false,
  dateProps = {},
  className = '',
  ...rest
}) => {
  // Handle rendering different input types
  const renderInput = () => {
    switch (type) {
      case 'dropdown':
        return (
          <Dropdown
            id={id}
            name={name}
            value={value}
            options={options}
            onChange={(e) => onChange({ target: { name, value: e.value } })}
            placeholder={placeholder}
            className={`w-100 ${hasError ? 'is-invalid' : ''}`}
            disabled={disabled}
            {...rest}
          />
        );

      case 'date':
        return (
          <Calendar
            id={id}
            name={name}
            value={value}
            onChange={(e) => onChange({ target: { name, value: e.value } })}
            dateFormat="dd/mm/yy"
            showIcon
            className={`w-100 ${hasError ? 'is-invalid' : ''}`}
            disabled={disabled}
            {...dateProps}
            {...rest}
          />
        );

      case 'textarea':
        return (
          <Form.Control
            as="textarea"
            id={id}
            name={name}
            value={value || ''}
            onChange={onChange}
            placeholder={placeholder}
            isInvalid={hasError}
            disabled={disabled}
            rows={4}
            {...rest}
          />
        );

      default:
        return icon ? (
          <InputGroup>
            <InputGroup.Text>{icon}</InputGroup.Text>
            <Form.Control
              type={type}
              id={id}
              name={name}
              value={value || ''}
              onChange={onChange}
              placeholder={placeholder}
              isInvalid={hasError}
              disabled={disabled}
              {...rest}
            />
          </InputGroup>
        ) : (
          <Form.Control
            type={type}
            id={id}
            name={name}
            value={value || ''}
            onChange={onChange}
            placeholder={placeholder}
            isInvalid={hasError}
            disabled={disabled}
            {...rest}
          />
        );
    }
  };

  return (
    <Form.Group className={`mb-3 ${className}`}>
      {label && <Form.Label>{label}</Form.Label>}
      {renderInput()}
      {hasError && <div className="invalid-feedback d-block">{errorMessage}</div>}
    </Form.Group>
  );
};

export default FormField;
